import { computed, ref } from 'vue'

import type { Ref, ComputedRef } from 'vue'
import type { MultiselectSearchOption } from '@shared/ui/fields'
import type { TaskType } from '@shared/config'

import { useListsStore } from '@shared/model'
import { AppRoute, TasksKanbanTeam } from '@shared/config'

interface FilterTaskTypeOptions {
  initial: string[]
}

interface FilterTaskTypeReturn {
  options: ComputedRef<MultiselectSearchOption[]>
  query: Ref<string[]>
  initialValue: ComputedRef<string[]>
  handleSelected: (selected: MultiselectSearchOption[]) => void
}

const teamByRoute: Record<string, TasksKanbanTeam> = {
  [AppRoute.TasksKanbanSupport]: TasksKanbanTeam.Support,
  [AppRoute.TasksKanbanSuccess]: TasksKanbanTeam.Success,
  [AppRoute.TasksKanbanBilling]: TasksKanbanTeam.Billing,
}

export const useFilterTaskType = ({ initial }: FilterTaskTypeOptions): FilterTaskTypeReturn => {
  const listsStore = useListsStore()
  const route = useRoute()

  const query = ref<string[]>(initial)

  const refCurrentTeam = computed<TasksKanbanTeam | null>(() => teamByRoute[route.path] ?? null)

  const options = computed<MultiselectSearchOption[]>(() => {
    const taskTypes = listsStore.getLists?.tasks_kanban.lists.task_type ?? {}
    const taskTypeConfigs = listsStore.getTaskTypes
    const team = refCurrentTeam.value

    return Object.entries(taskTypes)
      .filter(([key]) => {
        if (!team) return true
        return taskTypeConfigs?.[key as TaskType]?.team === team
      })
      .map(([key, text]) => ({
        id: key,
        value: key,
        text,
        selected: query.value.includes(key),
      }))
  })

  const initialValue = computed<string[]>(() => {
    return options.value
      .filter(option => option.selected)
      .map(option => option.text)
  })

  const handleSelected = (selected: MultiselectSearchOption[]): void => {
    query.value = selected.map(option => String(option.value))
  }

  return {
    options,
    query,
    initialValue,
    handleSelected,
  }
}
